import { useMemo } from 'react'
import { clipToDay, dayKey, hourLabel, hourSpan, isAllDay, layoutOverlaps, meetingsOnDay, minutesIntoDay, sameDay, weekDays } from '../../lib/calendar'
import type { MeetingView } from '../../types/calendar'
import { AllDayChip, WeekBlock } from './MeetingChip'

// The week: seven day columns over the working day, each meeting a block sized by its length and laid
// side by side where two overlap, all-day entries in their own row, today's column lit, and the "now"
// line moving down it. The hours stretch to cover an early breakfast or a late call.

const HOUR_PX = 56
const WEEKDAY = new Intl.DateTimeFormat('en-US', { weekday: 'short' })

export function WeekView({ anchor, meetings, now, selectedId, onPick, onOpenDay }: {
  anchor: Date; meetings: MeetingView[]; now: Date; selectedId: string | null; onPick: (m: MeetingView) => void; onOpenDay: (day: Date) => void
}) {
  const days = useMemo(() => weekDays(anchor), [anchor])
  const items = useMemo(() => meetings.map((m) => ({ ...m.meeting, view: m })), [meetings])
  const columns = useMemo(() => days.map((day) => {
    const all = meetingsOnDay(items, day)
    const allDay = all.filter((x) => isAllDay(x.start, x.end)).map((x) => x.view)
    const timed = all.filter((x) => !isAllDay(x.start, x.end)).map((x) => ({ view: x.view, ...clipToDay(x.start, x.end, day) }))
    const lanes = layoutOverlaps(timed.map((t) => ({ id: t.view.meeting.meetingId, start: t.top, end: t.top + t.height })))
    return { day, allDay, timed, lanes }
  }), [days, items])
  const span = useMemo(() => hourSpan(columns.flatMap((c) => c.timed)), [columns])
  const hours = Array.from({ length: span.last - span.first }, (_, i) => span.first + i)
  const hasAllDay = columns.some((c) => c.allDay.length > 0)
  const todayIndex = days.findIndex((d) => sameDay(d, now))
  const nowTop = todayIndex >= 0 ? (minutesIntoDay(now) - span.first * 60) / 60 * HOUR_PX : null

  return (
    <div className="cal-week" role="grid" aria-label="Week">
      <div className="cal-week-head" role="row">
        <div className="cal-gutter" />
        {days.map((day) => (
          <button key={dayKey(day)} className={`cal-week-daybtn ${sameDay(day, now) ? 'today' : ''} ${day.getDay() === 0 || day.getDay() === 6 ? 'weekend' : ''}`} onClick={() => onOpenDay(day)} title="Open this day on the agenda">
            <span className="cal-week-dow">{WEEKDAY.format(day)}</span>
            <span className="cal-week-dom">{day.getDate()}</span>
          </button>
        ))}
      </div>
      {hasAllDay && (
        <div className="cal-week-allday" role="row">
          <div className="cal-gutter cal-gutter-label">all day</div>
          {columns.map(({ day, allDay }) => (
            <div key={dayKey(day)} className={`cal-week-alldaycell ${sameDay(day, now) ? 'today' : ''}`}>
              {allDay.map((m) => <AllDayChip key={m.meeting.meetingId} m={m} now={now} selected={m.meeting.meetingId === selectedId} onPick={onPick} />)}
            </div>
          ))}
        </div>
      )}
      <div className="cal-week-body" style={{ height: hours.length * HOUR_PX }}>
        <div className="cal-gutter">
          {hours.map((h) => <div key={h} className="cal-hour-label" style={{ top: (h - span.first) * HOUR_PX }}>{hourLabel(h)}</div>)}
        </div>
        {columns.map(({ day, timed, lanes }, i) => (
          <div key={dayKey(day)} className={`cal-week-col ${sameDay(day, now) ? 'today' : ''} ${day.getDay() === 0 || day.getDay() === 6 ? 'weekend' : ''}`}>
            {hours.map((h) => <div key={h} className="cal-hour-line" style={{ top: (h - span.first) * HOUR_PX }} />)}
            {timed.map(({ view, top, height }) => {
              // Overlaps cascade: each later lane starts a step to the right and sits on top, so every title stays readable.
              const lane = lanes.get(view.meeting.meetingId) ?? { col: 0, cols: 1 }
              const left = lane.cols > 1 ? (lane.col * (100 / lane.cols)) * 0.7 : 0
              return (
                <WeekBlock key={view.meeting.meetingId} m={view} now={now} selected={view.meeting.meetingId === selectedId} onPick={onPick}
                  style={{ top: (top - span.first * 60) / 60 * HOUR_PX, height: Math.max(22, height / 60 * HOUR_PX - 2), left: `calc(${left}% + 2px)`, width: `calc(${100 - left}% - 4px)`, zIndex: 1 + lane.col }} />
              )
            })}
            {i === todayIndex && nowTop != null && nowTop >= 0 && nowTop <= hours.length * HOUR_PX && <div className="cal-now" style={{ top: nowTop }} aria-label="Now"><i /></div>}
          </div>
        ))}
      </div>
    </div>
  )
}
